import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import JobDetails from './JobDetails';
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink, useMutation, gql } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

// GraphQL mutation for generating a share token
const GENERATE_SHARE_TOKEN = gql`
  mutation GenerateShareToken($input: GenerateShareTokenInput!) {
    generateShareToken(input: $input) {
      token
      expiresAt
    }
  }
`;

// Create a separate Apollo client for public requests
const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL || 'http://localhost:3001/api/graphql',
  credentials: 'include', // Use 'include' to send credentials
  fetchOptions: {
    mode: 'cors'
  }
});

// Create the auth link (without requiring authentication)
const authLink = setContext((_, { headers }) => {
  logger.debug('Setting up public request headers for Apollo client');
  return {
    headers: {
      ...headers,
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  }
});

// Create a more robust Apollo Client for public requests with better error handling
const publicClient = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    }
  },
  connectToDevTools: process.env.NODE_ENV === 'development',
});

// Component that handles legacy share links by generating a token
function LegacyShareHandler({ jobId, onTokenGenerated, onError, navigate }) {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const [generateToken] = useMutation(GENERATE_SHARE_TOKEN, {
    client: publicClient,
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      logger.debug('Token generation completed successfully', { hasToken: !!data?.generateShareToken?.token });
      if (data?.generateShareToken?.token) {
        const token = data.generateShareToken.token;
        setIsLoading(false);
        onTokenGenerated(token);
      } else {
        logger.error('Token generation completed but no token returned');
        setErrorMessage('Server responded without a valid token');
        setIsLoading(false);
        onError('Could not generate access token. Server response was invalid.');
      }
    },
    onError: (error) => {
      logger.error('Error generating shared job token', {
        message: error.message,
        graphQLErrorCount: error.graphQLErrors?.length || 0,
        networkStatusCode: error.networkError?.statusCode,
      });

      setErrorMessage(error.message || 'Unknown error');
      setIsLoading(false);
      onError('Could not generate access token for this job: ' + (error.message || 'Unknown error'));
    }
  });

  useEffect(() => {
    logger.debug('Legacy share handler generating token', { jobId });

    let timeoutId;

    // Add a timeout to detect stalled requests
    timeoutId = setTimeout(() => {
      logger.error('Shared job token request timed out');
      setErrorMessage('Request timed out after 20 seconds. The server may be unavailable.');
      setIsLoading(false);
      onError('Token generation timed out. Please try again later.');
    }, 20000); // 20 second timeout - increased for better reliability

    // Generate a token for the job
    const generateTokenForJob = async () => {
      try {
        logger.debug('Sending shared job token mutation', { jobId });

        // Try the mutation with retries
        const result = await generateToken({
          variables: {
            input: { jobId }
          }
        });

        clearTimeout(timeoutId);
        logger.debug('Shared job token mutation returned', {
          hasToken: !!result.data?.generateShareToken?.token,
          errorCount: result.errors?.length || 0,
        });

        // Check for errors in the result
        if (result.errors?.length > 0) {
          logger.error('GraphQL errors in shared job token generation', {
            messages: result.errors.map((tokenError) => tokenError.message),
          });
          const errorMsg = result.errors[0].message || 'Unknown GraphQL error';
          setErrorMessage(errorMsg);
          setIsLoading(false);
          onError('Error generating token: ' + errorMsg);
        }
      } catch (error) {
        clearTimeout(timeoutId);
        logger.error('Caught error in shared job token generation', error);

        // Try to extract the detailed error message
        const graphQLErrors = error.graphQLErrors || [];
        const networkError = error.networkError;

        if (graphQLErrors.length > 0) {
          const detailed = graphQLErrors.map(e => e.message).join(', ');
          setErrorMessage(detailed);
          setIsLoading(false);
          onError('GraphQL error: ' + detailed);
        } else if (networkError) {
          setErrorMessage('Network error: ' + (networkError.message || 'Connection failed'));
          setIsLoading(false);
          onError('Network error: ' + (networkError.message || 'Connection failed'));
        } else {
          setErrorMessage(error.message || 'Unknown error');
          setIsLoading(false);
          onError('Error in token generation: ' + (error.message || 'Unknown error'));
        }
      }
    };

    generateTokenForJob();

    // Clean up the timeout if component unmounts
    return () => {
      clearTimeout(timeoutId);
    };
  }, [generateToken, jobId, onError]);

  if (errorMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex items-center justify-center text-red-500 mb-4">
            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-center text-gray-800 mb-2">Token Generation Error</h2>
          <p className="text-gray-600 text-center mb-6">Error: {errorMessage}</p>
          <div className="text-sm text-gray-500 text-center mb-4">
            Job ID: {jobId}
          </div>
          <div className="flex flex-col space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md transition-colors duration-300"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md transition-colors duration-300"
            >
              Log In To View
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mb-4"></div>
          <p className="text-gray-600">Preparing secure access to job details...</p>
          <p className="text-sm text-gray-400 mt-2">Job ID: {jobId}</p>
        </div>
      </div>
    );
  }

  return null;
}

export function SharedJobDetails() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isValidToken, setIsValidToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [legacyShareMode, setLegacyShareMode] = useState(false);
  const [generatedToken, setGeneratedToken] = useState(null);

  // Extract token from the URL or use generated token
  const token = searchParams.get('token') || generatedToken;
  const isLegacyShare = searchParams.get('share') === 'true';

  useEffect(() => {
    logger.debug('SharedJobDetails mounting', {
      jobId,
      hasToken: !!token,
      isLegacyShare,
      isLoggedIn: !!user
    });

    // Remove the redirect for logged-in users to allow them to view shared links too
    /*
    // If user is already logged in, we can just redirect them to the dashboard job view
    if (user) {
      navigate(`/dashboard/jobs/${jobId}`, { replace: true });
      return;
    }
    */

    // For public access, check if we have a token or need to generate one
    if (!token && !isLegacyShare) {
      logger.debug('No token or legacy share parameter found', { jobId });
      setError('Invalid or missing access token. This link may have expired.');
      setIsLoading(false);
      return;
    }

    // If we have a legacy share flag but no token, we need to generate one
    if (isLegacyShare && !token) {
      logger.debug('Legacy share mode detected', { jobId });
      setLegacyShareMode(true);
      setIsLoading(false);
      return;
    }

    // If we have a token, we can proceed
    if (token) {
      logger.debug('Shared job token found, proceeding with public view', { jobId });
      setIsValidToken(true);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  }, [token, isLegacyShare, navigate, jobId, user]);

  // Handle when a token is generated
  const handleTokenGenerated = (newToken) => {
    logger.debug('Shared job token generated successfully', { jobId });
    setGeneratedToken(newToken);
    setLegacyShareMode(false);
    setIsValidToken(true);

    // Update the URL to include the token (for better sharing)
    const newUrl = `${window.location.pathname}?token=${newToken}`;
    window.history.replaceState(null, '', newUrl);
  };

  const handleError = (errorMessage) => {
    logger.error('Error in shared job view', { message: errorMessage });
    setError(errorMessage);
    setLegacyShareMode(false);
    setIsValidToken(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (legacyShareMode) {
    return (
      <ApolloProvider client={publicClient}>
        <LegacyShareHandler
          jobId={jobId}
          onTokenGenerated={handleTokenGenerated}
          onError={handleError}
          navigate={navigate}
        />
      </ApolloProvider>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex items-center justify-center text-red-500 mb-4">
            <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-center text-gray-800 mb-2">Access Error</h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>

          <div className="flex flex-col space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-md transition-colors duration-300"
            >
              Try Again
            </button>

            <button
              onClick={() => navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`)}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md transition-colors duration-300"
            >
              Log In To View
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-md transition-colors duration-300"
            >
              Return to Home
            </button>
          </div>

          <div className="mt-6 text-xs text-gray-500 text-center">
            <p>Job ID: {jobId}</p>
            <p className="mt-1">Share type: {isLegacyShare ? 'Legacy' : 'Token-based'}</p>
            <p className="mt-1">Time: {new Date().toLocaleString()}</p>
          </div>
        </div>
      </div>
    );
  }

  if (isValidToken) {
    logger.debug('Rendering public JobDetails', { jobId });
    return (
      <div className="px-2 sm:px-4 md:px-6">
        <ApolloProvider client={publicClient}>
          <JobDetails isPublicView={true} accessToken={token} />
        </ApolloProvider>
      </div>
    );
  }

  // This should never happen, but just in case
  return null;
}
